"use client";

import {
  AnimatedMenuItem,
  AnimatedMenuSkeleton,
} from "@/components/layout/navigation/animated-menu-item";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import { useNavigation } from "@/contexts/navigation-context";
import {
  createNavigationLevel,
  findMenuItemById,
} from "@/lib/shared/menu-utils";
import { cn } from "@/lib/shared/utils";
import { NavigationItem } from "@/types/menu";
import { IconChevronLeft } from "@tabler/icons-react";
import { usePathname, useRouter } from "next/navigation";
import React from "react";

interface EnhancedNavigationProps {
  className?: string;
}

export function EnhancedNavigation({ className }: EnhancedNavigationProps) {
  const {
    navigationItems,
    currentLevel,
    navigateBack,
    setNavigationLevel,
    userRole,
    isLoading,
    isDashboardsLoading,
  } = useNavigation();
  const router = useRouter();
  const pathname = usePathname();

  // Simple rule: don't show menu until dashboards are loaded
  const showMenu = !isDashboardsLoading;

  const handleItemClick = (item: NavigationItem) => {
    if (item.children && item.children.length > 0) {
      // For items with children, find the menu config and navigate to appropriate level
      const menuConfig = findMenuItemById(item.id, userRole);
      if (menuConfig?.navigationLevel) {
        const level = createNavigationLevel(
          menuConfig.navigationLevel,
          currentLevel?.data
        );
        if (level) {
          setNavigationLevel(level);
          return;
        }
      }

      // Special handling for location settings (needs current location data)
      if (item.id === "location-settings") {
        const locationId = currentLevel?.data?.locationId;
        const locationName = currentLevel?.data?.locationName;
        if (locationId && locationName) {
          const level = createNavigationLevel("location-section", {
            locationId,
            locationName,
            section: "settings",
          });
          if (level) {
            setNavigationLevel(level);
          }
        }
      }
      return;
    }

    if (item.url && item.url !== "#") {
      router.push(item.url);
    }
  };

  return (
    <>
      <style jsx>{`
        @keyframes slideInFade {
          from {
            opacity: 0;
            transform: translateX(-8px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        .slide-in-item {
          animation: slideInFade 200ms ease-out forwards;
          opacity: 0;
        }
      `}</style>

      <div className={cn("flex flex-col h-full", className)}>
        {/* Back button - only show if we're not at the root level */}
        {currentLevel && showMenu && (
          <div className="slide-in-item" style={{ animationDelay: "0ms" }}>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton
                  onClick={navigateBack}
                  className="text-muted-foreground hover:text-foreground mb-2 transition-all duration-200 hover:scale-105"
                  tooltip="Back"
                >
                  <IconChevronLeft className="h-4 w-4" />
                  <span className="group-data-[collapsible=icon]:opacity-0 group-data-[collapsible=icon]:w-0 transition-all duration-200">Back</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </div>
        )}

        {/* Current level title */}
        {currentLevel && showMenu && (
          <div className="slide-in-item group-data-[collapsible=icon]:hidden" style={{ animationDelay: "50ms" }}>
            <div className="px-3 py-2 mb-2">
              <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
                {currentLevel.icon && <currentLevel.icon className="h-4 w-4" />}
                <span>{currentLevel.title}</span>
              </div>
            </div>
          </div>
        )}

        {/* Navigation items */}
        <div className="flex-1">
          {!showMenu ? (
            // Don't show anything until dashboards are loaded
            <div className="h-4" />
          ) : (
            <SidebarMenu>
              {isLoading ? (
                <AnimatedMenuSkeleton count={3} />
              ) : navigationItems.length === 0 ? (
                <div className="px-3 py-4 text-center text-muted-foreground text-sm">
                  No items available
                </div>
              ) : (
                navigationItems.map((item, index) => (
                  <div
                    key={item.id}
                    className="slide-in-item"
                    style={{
                      animationDelay: `${
                        (index + (currentLevel ? 2 : 0)) * 50
                      }ms`,
                    }}
                  >
                    <AnimatedMenuItem
                      item={item}
                      pathname={pathname}
                      onItemClick={handleItemClick}
                      index={index}
                    />
                  </div>
                ))
              )}
            </SidebarMenu>
          )}
        </div>
      </div>
    </>
  );
}
